import i18n from '../i18n'
import { jalaliMonthLength, parseIsoDate, toJalali } from './jalali'
import { faDigits } from './numerals'
import { persianMonths } from './profileForm'
import { faDecimal } from './social'

/**
 * The leaderboard's rules on the client (V6-w), over `social_leaderboard`
 * as V6-b-leaderboard extended it. The server decides every rank; this file
 * only picks which request to send and how to say a row's number.
 *
 * The switch is the server's answer, not a client flag: the page always
 * asks the legacy `weekly` / `monthly` board first — exactly today's
 * request. While the owner keeps `leaderboard.calendarPeriodsEnabled` off
 * (production until stage 3) that answer is the rolling 7/30-day board with
 * no `periodKey`, and the page stays exactly as it was. Once it is on, the
 * same answer carries the calendar fields (`periodKey`, `category`, …), and
 * only then does the page offer the other categories, which it asks for
 * with `period=week|month&category=…`.
 */

const t = (key, options) => i18n.t(key, { ns: 'leaderboard', ...options })

/** One decimal: «۴٫۵» in Persian, «4.5» in English. */
export function oneDecimal(value) {
  const number = Number(value) || 0
  return i18n.language === 'fa' ? faDecimal(number) : number.toFixed(1)
}

/** The categories of V6-b-leaderboard, in the order the tabs show them. */
export const CATEGORIES = ['xp', 'team', 'improved', 'popular', 'teams']

/** Labels are functions of the active language. */
export const CATEGORY_OPTIONS = () => CATEGORIES.map((key) => ({ key, label: t(`categories.${key}`) }))

/** Whether an answer is a calendar board — the additive fields are present only on those. */
export function isCalendarBoard(answer) {
  return typeof answer?.periodKey === 'string' && answer.periodKey.length > 0
}

/** The calendar period behind a legacy pill: `weekly` → `week`, `monthly` → `month`. */
export function calendarPeriod(period) {
  return period === 'monthly' ? 'month' : 'week'
}

/**
 * The request for a pill and a category. XP — and anything while the server
 * has not shown calendar boards — is the legacy request, unchanged; the other
 * categories are calendar requests.
 */
export function boardRequest(period, category, calendar) {
  if (!calendar || !category || category === 'xp') return { period, options: undefined }
  return { period: calendarPeriod(period), options: { category } }
}

/** A row's number as the category means it. */
export function entryValueLabel(category, entry) {
  const value = typeof entry?.value === 'number' ? entry.value : entry?.xp ?? 0
  switch (category) {
    case 'improved':
      return t('value.improved', { value: oneDecimal(value) })
    case 'popular':
      return t('value.popular', { value: faDigits(Math.round(value)) })
    default:
      return t('xpSuffix', { xp: faDigits(entry?.xp ?? 0) })
  }
}

/** A team row's number: the members' average XP, one decimal. */
export function teamValueLabel(row) {
  const value = typeof row?.value === 'number' ? row.value : 0
  return t('value.teams', { value: Number.isInteger(value) ? faDigits(value) : oneDecimal(value) })
}

/** The empty-state sentence for a board with no rows. */
export function emptyLabel(period, category, calendar) {
  if (!calendar || !category || category === 'xp') return period === 'monthly' ? t('emptyMonthly') : t('emptyWeekly')
  return t(`empty.${category}`)
}

/** [days] (under a month) after a Jalali date, rolling into the next month and year. */
function plusDays(date, days) {
  const length = jalaliMonthLength(date.year, date.month)
  const day = date.day + days
  if (day <= length) return { ...date, day }
  return date.month === 12 ? { year: date.year + 1, month: 1, day: day - length } : { year: date.year, month: date.month + 1, day: day - length }
}

/**
 * The span a calendar board covers, from its `periodKey`: `W2026-09-26` (a
 * week, by its Saturday, Gregorian) is seven Jalali days from that Saturday;
 * `1405-07` is a Jalali month. Null for anything else.
 */
export function periodSpanOf(periodKey) {
  const key = String(periodKey ?? '').trim()
  if (key.startsWith('W')) {
    const saturday = parseIsoDate(key.slice(1))
    if (!saturday) return null
    const start = toJalali(saturday.year, saturday.month, saturday.day)
    return { kind: 'week', start, end: plusDays(start, 6) }
  }
  const parts = key.split('-')
  if (parts.length !== 2) return null
  const year = Number(parts[0])
  const month = Number(parts[1])
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12 || year < 1) return null
  return { kind: 'month', year, month }
}

/**
 * The line under the title (the Android rule, `LeaderboardScreen.kt`): the
 * span the calendar board covers - «۱۲ مهر تا ۱۸ مهر», «مهر ۱۴۰۵» - and, on
 * the rolling board that has no span, the fixed short line.
 */
export function subtitleOf(calendar, periodKey) {
  const span = calendar ? periodSpanOf(periodKey) : null
  if (!span) return t('subtitle')
  const dayMonth = (date) => t('dayMonth', { day: faDigits(date.day), month: persianMonths()[date.month - 1] })
  if (span.kind === 'week') return t('weekRange', { from: dayMonth(span.start), to: dayMonth(span.end) })
  return t('monthYear', { month: persianMonths()[span.month - 1], year: faDigits(span.year) })
}
